// AI Generated Component
import { Component, Input, OnInit, OnChanges } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PageRepoService } from '@client/app/services/api/page-repo/page-repo.service';
import { QAReadabilityService } from '@client/app/blocks/service/qa-readability/qareadability.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { READABILITY_HELPER } from '@client/app/core/constants/helpers/readability-helper';

@Component({
    selector: 'readability-summary-results',
    standalone: false,
    template: `
        <div class="row">
            <div class="no-result-message-container p-4 text-center col-48" *ngIf="!doughnutData[0]">
                <p class="font-weight-300 text-grey" translate>No data available</p>
            </div>
            <div *ngIf="doughnutData[0]" class="col-48 col-md-30">
                <div class="p-4">
                    <mon-chart-horizontal-bar
                            [labels]="barLabels"
                            [series]="barSeries"
                            [options]="barOptions"
                            [data]="barData"
                            [colors]="barColors"
                            [override]="barOverrides"
                    >
                    </mon-chart-horizontal-bar>
                </div>
            </div>
            <div *ngIf="doughnutData[0]" class="col-48 col-md-18">
                <div class="p-4">
                    <span *ngIf="summary.distribution.length > 0" class="text-center">
                        <p class="text-grey" translate>Most of your pages have the readability level</p>
                        <h3>{{ getTranslationScore(summary.distribution[0].name, summary.test) }}</h3>
                    </span>
                    <div class="inner-all-large readability-doughnut">
                        <mon-chart-doughnut
                                [labels]="doughnutLabels"
                                [options]="doughnutOptions"
                                [data]="doughnutData"
                                [colors]="doughnutColors"
                        >
                        </mon-chart-doughnut>
                    </div>
                </div>
            </div>
        </div>
    `,
})
export class ReadabilitySummaryResultsComponent implements OnInit, OnChanges {
    @Input() summary: any;

    barLabels: any[] = [];
    barSeries: string[] = ['Pages'];
    barOptions: any = {};
    barData: any[] = [];
    barColors: string[] = ['#239CF9'];
    barOverrides: any[] = [];

    doughnutLabels: string[] = ['Pages in compliance', 'Pages with errors'];
    doughnutColors: string[] = ['#239CF9', '#DCDCDC'];
    doughnutData: any[] = [];
    doughnutOptions: any = {};

    totalPageCount: number = 0;
    type: string = '';

    constructor (
        private translateService: TranslateService,
        private pageRepoService: PageRepoService,
        private qaReadabilityService: QAReadabilityService,
        private pipeService: PipeService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    ngOnChanges (): void {
        if (this.type && this.summary) {
            this.setupDoughnutChart();
            this.setupBarChart();
        }
    }

    getTranslationScore (score: string, readabilityType: string): string {
        return this.qaReadabilityService.getTranslationScore(score, readabilityType);
    }

    private activate (): void {
        this.getPages().then((values: any) => {
            this.totalPageCount = parseInt(values.total, 10);
            this.type = this.summary.test;
            this.setupDoughnutChart();
            this.setupBarChart();
        }, () => {});
    }

    private getPages (): Promise<any> {
        const params = {
            page_size: 1,
            page: 1,
        };

        return this.pageRepoService.getAll(params);
    }

    private setupDoughnutChart (): void {
        this.barLabels = [];
        this.barSeries = ['Pages'];
        this.barOptions = {
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    boxWidth: 0,
                    padding: 0,
                },
            },
            scales: {
                xAxes: [
                    {
                        gridLines: {
                            offsetGridLines: true,
                            display: false,
                        },
                        ticks: {
                            beginAtZero: true,
                            suggestedMax: 4,
                            maxTicksLimit: 8,
                            callback: (value: any) => {
                                return this.pipeService.get('monNumber')(value, '0,0');
                            },
                            min: 0,
                        },
                    },
                ],
            },
        };
        this.barOverrides = [
            {
                label: 'Pages',
                backgroundColor: [],
                borderColor: [],
            },
        ];
        this.barColors = ['#239CF9'];
        const s1: any[] = [];
        const distributions = this.summary.plain_distribution;

        for (let i = 0; i < READABILITY_HELPER[this.type].length; i++) {
            const level = READABILITY_HELPER[this.type][i];
            this.makeChartLabel(level, distributions, s1);
        }
        for (let i = 0; i < READABILITY_HELPER.na_reasons[this.type]?.length; i++) {
            const level = READABILITY_HELPER.na_reasons[this.type][i];
            this.makeChartLabel(level, distributions, s1);
        }
        this.barData = [s1];
    }

    private setupBarChart (): void {
        const _sortedDistribution = this.sortedDistribution(this.summary.distribution)[0];
        const pagesWithHeightsDistribution = _sortedDistribution && _sortedDistribution.page_count;
        this.doughnutLabels = ['Pages in compliance', 'Pages with errors'];
        this.doughnutColors = ['#239CF9', '#DCDCDC'];

        this.doughnutData = [pagesWithHeightsDistribution, this.totalPageCount - pagesWithHeightsDistribution];
        // If the result isn't a number(NaN), so return 0.
        let result = pagesWithHeightsDistribution / this.totalPageCount;
        result = isFinite(result) ? result : 0;
        const percentage = this.pipeService.get('monNumber')(result, '0.[00] %');
        this.doughnutOptions = {
            centerText: {
                percentageLabel: percentage,
                totalLabel: '  ', // Keep it, otherwise the text on the doughnut disappears
                subLabel:
                    '(' +
                    this.translateService.getString('Affects') +
                    ' ' +
                    pagesWithHeightsDistribution +
                    ' ' +
                    this.pipeService.get('translateWithPlural')('page', pagesWithHeightsDistribution, 'pages') +
                    ')',
            },
            cutoutPercentage: 68,
            tooltips: {
                callbacks: {
                    label: (tooltipItem: any, data: any) => {
                        return this.translateService.getString('[[pageCount]] Pages', {
                            pageCount: this.pipeService.get('monNumber')(data.datasets[0].data[tooltipItem.index], '0,0'),
                        });
                    },
                },
            },
        };
    }

    private sortedDistribution (distribution: any[]): any[] {
        return distribution.sort((a: any, b: any) => {
            if (a.page_count < b.page_count) {
                return 1;
            }
            if (a.page_count > b.page_count) {
                return -1;
            }
            return 0;
        });
    }

    private makeChartLabel (level: any, distributions: any, s1: any[]): void {
        const subTitle = this.qaReadabilityService.getReadabilityLevelSubTitle(level, this.summary.test);
        let label: any;
        if (subTitle) {
            label = [this.qaReadabilityService.getTranslationScore(level, this.type), '(' + subTitle + ')'];
        } else {
            label = this.qaReadabilityService.getTranslationScore(level, this.type);
        }
        this.barLabels.push(label);
        if (distributions[level]) {
            s1.push(distributions[level]);
        } else {
            s1.push(0);
        }
        this.barOverrides[0].backgroundColor.push('rgba(35,156,249, 1)');
        this.barOverrides[0].borderColor.push('rgba(35,156,249, 1)');
    }
}
